import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { assert } from '@varos/util-typescript';
import { getTransform } from '../../common/tooltip/tooltipHelper';
import { formatValue } from '../../support';
export function createTrendTooltipController(config) {
    return {
        useProps(props) {
            const tooltipElRef = useRef(null);
            const tooltipRootRef = useRef(null);
            const [tooltipContent, setTooltipContent] = useState(null);
            const hideTooltipTimeoutRef = useRef(null);
            const externalTooltipHandler = useCallback((context) => {
                const { chart, tooltip } = context;
                // Hide tooltip if not visible
                if (tooltip.opacity === 0) {
                    if (tooltipContent !== null) {
                        setTooltipContent(null);
                    }
                    return;
                }
                // // Set tooltip content
                // const titleLines = tooltip.title || [];
                // Position the tooltip
                const position = chart.canvas.getBoundingClientRect();
                const [dataPoint] = tooltip.dataPoints;
                assert(dataPoint, 'expected data point');
                const dataset = props.datasets[dataPoint.datasetIndex];
                assert(dataset, 'expected dataset');
                const label = props.labels[dataPoint.dataIndex];
                let labelText = 'N/A';
                if (label && props.axes.time.unit === 'day') {
                    labelText = config.strategy.format.formatLongDate(label.start.raw);
                }
                else {
                    labelText = label?.range.label ?? 'N/A';
                }
                const value = dataset.data[dataPoint.dataIndex] ?? null;
                const previousValue = dataset.data[dataPoint.dataIndex - 1] ?? null;
                const formattedValue = formatValue(config.strategy.format, props.axes.scalar.format ?? null, value, {
                    notation: 'long',
                });
                const formattedPreviousValue = previousValue !== null
                    ? formatValue(config.strategy.format, props.axes.scalar.format ?? null, previousValue, {
                        notation: 'long',
                    })
                    : null;
                let changeValue = null;
                let changeFormatted = null;
                if (value !== null && previousValue !== null && previousValue > 0) {
                    changeValue =
                        value !== null && previousValue !== null
                            ? (value - previousValue) / previousValue
                            : null;
                    changeFormatted =
                        changeValue !== null
                            ? formatValue(config.strategy.format, 'percentage', changeValue, {
                                notation: 'long',
                            })
                            : null;
                }
                else if (value !== null && previousValue === 0) {
                    changeValue = 0;
                    changeFormatted = 'N/A';
                }
                if (changeValue && changeValue > 0 && changeFormatted) {
                    changeFormatted = `+${changeFormatted}`;
                }
                const newTooltipContent = {
                    label: labelText,
                    left: position.left + window.pageXOffset + tooltip.caretX,
                    top: position.top + window.pageYOffset + tooltip.caretY,
                    xAlign: tooltip.xAlign,
                    yAlign: tooltip.yAlign,
                    dataset: dataset,
                    data: {
                        raw: value,
                        formattedValue,
                    },
                    previous: previousValue && formattedPreviousValue
                        ? {
                            raw: previousValue,
                            formattedValue: formattedPreviousValue,
                        }
                        : null,
                    change: changeValue && changeFormatted
                        ? {
                            raw: changeValue,
                            formattedValue: changeFormatted,
                            color: changeValue > 0 ? 'green' : 'red',
                        }
                        : null,
                    dataIndex: dataPoint.dataIndex,
                };
                // Only update state if content has changed
                if (!tooltipContent ||
                    tooltipContent.left !== newTooltipContent.left ||
                    tooltipContent.top !== newTooltipContent.top ||
                    tooltipContent.label !== newTooltipContent.label) {
                    setTooltipContent(newTooltipContent);
                }
            }, [tooltipContent]);
            useEffect(() => {
                const chart = props.chartRef.current;
                if (!chart)
                    return;
                let tooltipEl = tooltipElRef.current;
                if (!tooltipEl) {
                    tooltipEl = document.createElement('div');
                    tooltipElRef.current = tooltipEl;
                    tooltipEl.id = 'chartjs-tooltip';
                    tooltipEl.classList.add('chartjs-tooltip');
                    tooltipEl.style.position = 'absolute';
                    tooltipEl.style.pointerEvents = 'none';
                    tooltipEl.style.transition = 'opacity 0.1s ease';
                    document.body.appendChild(tooltipEl);
                    // Create React root for the tooltip
                    tooltipRootRef.current = ReactDOM.createRoot(tooltipEl);
                }
                return () => {
                    if (tooltipEl) {
                        tooltipRootRef.current?.unmount();
                        tooltipEl.remove();
                    }
                    // Clear any pending hide tooltip timeout
                    if (hideTooltipTimeoutRef.current !== null) {
                        clearTimeout(hideTooltipTimeoutRef.current);
                        hideTooltipTimeoutRef.current = null;
                    }
                };
            }, []);
            const interactionMode = config.options.interaction;
            if (!tooltipContent) {
                return {
                    visible: false,
                    chartjs: {
                        tooltip: { enabled: false, external: externalTooltipHandler },
                        interaction: { mode: interactionMode },
                    },
                };
            }
            return {
                visible: true,
                state: tooltipContent,
                chartjs: {
                    tooltip: { enabled: false, external: externalTooltipHandler },
                    interaction: { mode: interactionMode },
                },
                root: {
                    position: 'absolute',
                    left: `${tooltipContent.left}px`,
                    top: `${tooltipContent.top}px`,
                    transform: getTransform(tooltipContent.xAlign, tooltipContent.yAlign),
                },
                custom: interactionMode === 'nearest'
                    ? {
                        mode: interactionMode,
                        label: tooltipContent.label,
                        dataset: tooltipContent.dataset,
                        data: tooltipContent.data,
                        previous: tooltipContent.previous,
                        change: tooltipContent.change,
                        axes: props.axes,
                    }
                    : {
                        mode: interactionMode,
                        label: tooltipContent.label,
                        datasets: props.datasets,
                        dataIndex: tooltipContent.dataIndex,
                        axes: props.axes,
                    },
            };
        },
    };
}
// function getRelativeValue(formatter:FormatStrategy, state: TooltipState) {
//     const previous = state.dataset.data[state.dataIndex - 1] ?? null;
//     if (previous === null) {
//         return null;
//     }
//     return {
//         raw: previous,
//         formattedValue: formatter.,
//     };
// }
//# sourceMappingURL=trendTooltipController.js.map